import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Drawer,
  Divider,
  AppBar,
  Toolbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { API } from '../API/api';
import Echo from '../config/echo';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Fix pour les icônes Leaflet avec React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * Créer une icône de camion personnalisée selon le statut
 */
const createTruckIcon = (status) => {
  const statusColors = {
    'draft': '#9e9e9e',
    'assigned': '#3c2151',
    'on_way_to_pickup': '#f59e0b',
    'loading': '#06b6d4',
    'in_transit': '#3c2151',
    'customs': '#f59e0b',
    'ferry': '#8b5cf6',
    'unloading': '#10b981',
    'completed': '#10b981',
    'cancelled': '#ef4444',
  };

  const color = statusColors[status] || '#9e9e9e';

  const svgIcon = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="${color}" stroke="#fff" stroke-width="3" opacity="0.9"/>
      <path d="M10 22 L10 14 L16 14 L18 8 L26 8 L28 14 L32 14 L32 22 L30 22 L30 26 L26 26 L26 22 L14 22 L14 26 L10 26 L10 22 Z" fill="#fff" stroke="#000" stroke-width="0.5"/>
      <circle cx="14" cy="24" r="2.5" fill="#000"/>
      <circle cx="26" cy="24" r="2.5" fill="#000"/>
      <rect x="12" y="16" width="8" height="4" fill="#fff" opacity="0.8"/>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-truck-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

/**
 * Composant pour ajuster la vue de la carte
 */
function MapBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [bounds, map]);
  
  return null;
}

/**
 * Retourne la couleur du chip selon le statut
 */
const getStatusColor = (status) => {
  const colors = {
    'draft': 'default',
    'assigned': 'info',
    'on_way_to_pickup': 'warning',
    'loading': 'info',
    'in_transit': 'primary',
    'customs': 'warning',
    'ferry': 'secondary',
    'unloading': 'success',
    'completed': 'success',
    'cancelled': 'error',
  };
  return colors[status] || 'default';
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapBounds, setMapBounds] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    inTransit: 0,
    completed: 0,
    pending: 0,
  });

  // S'assurer que stats est toujours défini
  const safeStats = stats || {
    total: 0,
    inTransit: 0,
    completed: 0,
    pending: 0,
  };

  // Récupérer le token depuis sessionStorage
  const getToken = useCallback(() => {
    try {
      const storedAuthState = sessionStorage.getItem('authLocalstorage');
      if (storedAuthState) {
        const parsedAuthState = JSON.parse(storedAuthState);
        return parsedAuthState?.token;
      }
    } catch (e) {
      console.error('Error parsing auth state:', e);
    }
    return null;
  }, []);

  // Charger les informations du client
  const fetchClientInfo = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('No token found');
        navigate('/');
        return;
      }

      const response = await axios.get(`${API}/client-portal/client-info`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (response.data && response.data.status) {
        setClientInfo(response.data.data || {});
      } else {
        const errorMessage = response.data?.message || 'Erreur lors du chargement des informations du client';
        console.error('Client info error:', response.data);
        toast.error(errorMessage);
        setClientInfo({}); // Set empty object to prevent undefined errors
      }
    } catch (error) {
      console.error('Error fetching client info:', error);
      
      // Si erreur 401, rediriger vers login
      if (error.response?.status === 401) {
        sessionStorage.removeItem('authLocalstorage');
        toast.error('Session expirée. Veuillez vous reconnecter.');
        navigate('/');
        return;
      }

      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.debug?.message ||
                           'Erreur lors du chargement des informations du client';
      toast.error(errorMessage);
      setClientInfo({}); // Set empty object to prevent undefined errors
    }
  }, [getToken, navigate]);

  // Charger les voyages
  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        console.error('No token found');
        navigate('/');
        return;
      }

      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await axios.get(`${API}/client-portal/trips`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        params,
      });

      if (response.data && response.data.status) {
        const tripsData = Array.isArray(response.data.data) ? response.data.data : [];
        setTrips(tripsData);
        setFilteredTrips(tripsData);

        // Calculer les statistiques
        const newStats = {
          total: tripsData.length,
          inTransit: tripsData.filter(t => ['in_transit', 'on_way_to_pickup', 'loading', 'customs', 'ferry', 'unloading'].includes(t.statut)).length,
          completed: tripsData.filter(t => t.statut === 'completed').length,
          pending: tripsData.filter(t => ['draft', 'assigned'].includes(t.statut)).length,
        };
        setStats(newStats);

        // Calculer les bounds de la carte pour afficher tous les camions
        if (tripsData.length > 0) {
          const activeTrips = tripsData.filter(
            trip => trip.current_lat && trip.current_lng && 
            !['completed', 'cancelled'].includes(trip.statut)
          );

          if (activeTrips.length > 0) {
            const lats = activeTrips.map(t => parseFloat(t.current_lat));
            const lngs = activeTrips.map(t => parseFloat(t.current_lng));
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);
            setMapBounds([[minLat, minLng], [maxLat, maxLng]]);
          }
        }
      } else {
        const errorMessage = response.data?.message || 'Erreur lors du chargement des voyages';
        console.error('Trips error:', response.data);
        toast.error(errorMessage);
        setTrips([]); // Set empty array to prevent undefined errors
        setFilteredTrips([]);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      
      // Si erreur 401, rediriger vers login
      if (error.response?.status === 401) {
        sessionStorage.removeItem('authLocalstorage');
        toast.error('Session expirée. Veuillez vous reconnecter.');
        navigate('/');
        return;
      }

      const errorMessage = error.response?.data?.message || 
                           error.response?.data?.debug?.message ||
                           'Erreur lors du chargement des voyages';
      toast.error(errorMessage);
      setTrips([]); // Set empty array to prevent undefined errors
      setFilteredTrips([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, statusFilter, searchQuery, navigate]);

  // Charger les données au montage
  useEffect(() => {
    fetchClientInfo().catch(err => {
      console.error('Failed to fetch client info:', err);
    });
  }, [fetchClientInfo]);

  useEffect(() => {
    fetchTrips().catch(err => {
      console.error('Failed to fetch trips:', err);
    });
  }, [fetchTrips]);

  // Écouter les événements WebSocket pour mise à jour temps réel
  useEffect(() => {
    // Vérifier si Echo est disponible et fonctionnel
    if (!Echo || typeof Echo.channel !== 'function') {
      // WebSocket non disponible - mode dégradé (sans temps réel)
      return;
    }
    
    let channel;
    let isConnected = false;
    
    try {
      channel = Echo.channel('trips.tracking');
      
      if (channel && typeof channel.listen === 'function') {
        channel.listen('.trip.location.updated', (data) => {
          if (data && data.trip_id) {
            setTrips(prevTrips => {
              return prevTrips.map(trip => {
                if (trip.ID_Voyage === data.trip_id) {
                  return {
                    ...trip,
                    current_lat: data.current_lat,
                    current_lng: data.current_lng,
                    last_mobile_sync: data.last_mobile_sync,
                    statut: data.status || trip.statut,
                  };
                }
                return trip;
              });
            });
          }
        });
        isConnected = true;
      }
    } catch (error) {
      // Erreur silencieuse - WebSocket est optionnel
      // L'application fonctionne sans temps réel
    }

    return () => {
      if (channel && isConnected) {
        try {
          if (typeof channel.stopListening === 'function') {
            channel.stopListening('.trip.location.updated');
          }
          if (Echo && typeof Echo.leave === 'function') {
            Echo.leave('trips.tracking');
          }
        } catch (error) {
          // Erreur silencieuse lors du nettoyage
        }
      }
    };
  }, []);

  // Télécharger la POD
  const handleDownloadPod = async (tripId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/client-portal/trips/${tripId}/pod`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `POD-Voyage-${tripId}.jpg`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('POD téléchargée avec succès');
    } catch (error) {
      console.error('Error downloading POD:', error);
      toast.error('Erreur lors du téléchargement de la POD');
    }
  };

  // Gérer la déconnexion
  const handleLogout = () => {
    sessionStorage.removeItem('authLocalstorage');
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  // Ouvrir le drawer de détails
  const handleTripClick = (trip) => {
    setSelectedTrip(trip);
    setDrawerOpen(true);
  };

  // Filtrer les voyages actifs pour la carte
  const activeTrips = filteredTrips.filter(
    trip => trip.current_lat && trip.current_lng && !['completed', 'cancelled'].includes(trip.statut)
  );

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      {/* Header avec navigation */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {clientInfo?.logo && (
              <Avatar
                src={clientInfo.logo}
                alt={clientInfo.group_name || clientInfo.client_name}
                sx={{ 
                  width: 56, 
                  height: 56,
                  borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                }}
                variant="rounded"
              />
            )}
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1f2937',
                  letterSpacing: '-0.025em',
                  fontSize: '1.25rem',
                }}
              >
                Portail Client
              </Typography>
              {clientInfo?.client_name && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#6b7280',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  {clientInfo.client_name}
                  {clientInfo?.group_name && ` • ${clientInfo.group_name}`}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-trip-request')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 2.5,
                py: 1,
                background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                boxShadow: '0 2px 8px rgba(60, 33, 81, 0.25)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2a173a 0%, #3c2151 100%)',
                  boxShadow: '0 4px 12px rgba(60, 33, 81, 0.35)',
                },
              }}
            >
              Nouvelle Demande
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchTrips}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                borderColor: '#e5e7eb',
                color: '#1f2937',
                px: 2.5,
                py: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#3c2151',
                  backgroundColor: 'rgba(60, 33, 81, 0.08)',
                  boxShadow: '0 2px 8px rgba(60, 33, 81, 0.15)',
                },
              }}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                borderColor: '#e5e7eb',
                color: '#1f2937',
                px: 2.5,
                py: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
                },
              }}
            >
              Déconnexion
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Statistiques */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(60, 33, 81, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '2rem' }}>
                      {safeStats.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                      Total Expéditions
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      background: 'rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <TruckIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(60, 33, 81, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '2rem' }}>
                      {safeStats.inTransit}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                      En Transit
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      background: 'rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <LocationIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '2rem' }}>
                      {safeStats.completed}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                      Terminées
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      background: 'rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '2rem' }}>
                      {safeStats.pending}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                      En Attente
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      background: 'rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 36, opacity: 0.9 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtres et recherche */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06)',
            mb: 3,
            background: '#ffffff',
            border: '1px solid rgba(229, 231, 235, 0.8)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Rechercher par référence, origine, destination..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Statut"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value="all">Tous les statuts</MenuItem>
                    <MenuItem value="draft">Brouillon</MenuItem>
                    <MenuItem value="assigned">Assigné</MenuItem>
                    <MenuItem value="on_way_to_pickup">En route (Chargement)</MenuItem>
                    <MenuItem value="loading">Chargement</MenuItem>
                    <MenuItem value="in_transit">En transit</MenuItem>
                    <MenuItem value="customs">En Douane</MenuItem>
                    <MenuItem value="ferry">Passage Ferry</MenuItem>
                    <MenuItem value="unloading">Déchargement</MenuItem>
                    <MenuItem value="completed">Terminé</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchTrips}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                    boxShadow: '0 2px 8px rgba(60, 33, 81, 0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2a173a 0%, #3c2151 100%)',
                      boxShadow: '0 4px 12px rgba(60, 33, 81, 0.35)',
                    },
                  }}
                >
                  <FilterIcon sx={{ mr: 1 }} />
                  Filtrer
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tableau des expéditions */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06)',
            mb: 4,
            background: '#ffffff',
            border: '1px solid rgba(229, 231, 235, 0.8)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                fontWeight: 600,
                color: '#1f2937',
                letterSpacing: '-0.025em',
                mb: 3,
              }}
            >
              Liste des Expéditions
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTrips.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Aucune expédition trouvée
              </Alert>
            ) : (
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  mt: 2,
                  borderRadius: 1,
                  boxShadow: 'none',
                  border: '1px solid #e5e7eb',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Référence</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Statut</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Origine</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Destination</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>ETA</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Chauffeur</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Véhicule</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#1f2937' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTrips.map((trip) => (
                      <TableRow
                        key={trip.ID_Voyage}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(60, 33, 81, 0.04)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                          },
                        }}
                        onClick={() => handleTripClick(trip)}
                      >
                        <TableCell sx={{ color: '#1f2937', fontWeight: 500 }}>{trip.reference}</TableCell>
                        <TableCell>
                          <Chip
                            label={trip.statut_label}
                            color={getStatusColor(trip.statut)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#1f2937' }}>{trip.origine}</TableCell>
                        <TableCell sx={{ color: '#1f2937' }}>{trip.destination}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{trip.eta || 'N/A'}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{trip.driver_name || 'N/A'}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{trip.vehicule_matricule || 'N/A'}</TableCell>
                        <TableCell align="center">
                          {trip.pod_photo_url ? (
                            <Tooltip title="Télécharger la POD">
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#3c2151',
                                  '&:hover': {
                                    backgroundColor: 'rgba(60, 33, 81, 0.1)',
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPod(trip.ID_Voyage);
                                }}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '12px' }}>
                              POD non disponible
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Carte de monitoring live */}
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06)',
            background: '#ffffff',
            border: '1px solid rgba(229, 231, 235, 0.8)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontWeight: 600,
                color: '#1f2937',
                letterSpacing: '-0.025em',
                mb: 2,
              }}
            >
              <LocationIcon sx={{ color: '#3c2151' }} /> Monitoring Live - Camions en Mouvement
            </Typography>
            <Box sx={{ height: 500, width: '100%', mt: 2, borderRadius: 1, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {activeTrips.length > 0 ? (
                <MapContainer
                  center={[33.5731, -7.5898]}
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {mapBounds && <MapBounds bounds={mapBounds} />}
                  {activeTrips.map((trip) => (
                    <Marker
                      key={trip.ID_Voyage}
                      position={[parseFloat(trip.current_lat), parseFloat(trip.current_lng)]}
                      icon={createTruckIcon(trip.statut)}
                      eventHandlers={{
                        click: () => handleTripClick(trip),
                      }}
                    >
                      <Popup>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Voyage #{trip.reference}
                          </Typography>
                          <Typography variant="body2">
                            Statut: {trip.statut_label}
                          </Typography>
                          <Typography variant="body2">
                            Origine: {trip.origine}
                          </Typography>
                          <Typography variant="body2">
                            Destination: {trip.destination}
                          </Typography>
                          {trip.driver_name && (
                            <Typography variant="body2">
                              Chauffeur: {trip.driver_name}
                            </Typography>
                          )}
                          {trip.last_mobile_sync && (
                            <Typography variant="caption" color="text.secondary">
                              Dernière mise à jour: {trip.last_mobile_sync}
                            </Typography>
                          )}
                        </Box>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <TruckIcon sx={{ fontSize: 64, color: '#6b7280' }} />
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Aucun camion en mouvement pour le moment
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Drawer de détails du voyage */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            p: 0,
            background: '#ffffff',
            boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {selectedTrip && (
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                p: 3,
                pb: 2,
                background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                color: 'white',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', letterSpacing: '-0.02em' }}>
                Détails du Voyage
              </Typography>
              <IconButton 
                onClick={() => setDrawerOpen(false)} 
                size="small"
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <CancelIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(60, 33, 81, 0.05) 0%, rgba(93, 52, 119, 0.05) 100%)',
                  border: '1px solid rgba(60, 33, 81, 0.1)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  RÉFÉRENCE
                </Typography>
                <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 700, mt: 0.5, letterSpacing: '-0.02em' }}>
                  {selectedTrip.reference}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  STATUT
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedTrip.statut_label}
                    color={getStatusColor(selectedTrip.statut)}
                    size="medium"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      height: 32,
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ORIGINE
                </Typography>
                <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                  {selectedTrip.origine}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  DESTINATION
                </Typography>
                <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                  {selectedTrip.destination}
                </Typography>
              </Box>

              {selectedTrip.date_chargement && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    DATE DE CHARGEMENT
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                    {selectedTrip.date_chargement}
                  </Typography>
                </Box>
              )}

              {selectedTrip.date_dechargement && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    DATE DE DÉCHARGEMENT
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                    {selectedTrip.date_dechargement}
                  </Typography>
                </Box>
              )}

              {selectedTrip.eta && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.1)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    ETA (ARRIVÉE PRÉVUE)
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#059669', mt: 1, fontWeight: 600 }}>
                    {selectedTrip.eta}
                  </Typography>
                </Box>
              )}

              {selectedTrip.driver_name && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    CHAUFFEUR
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                    {selectedTrip.driver_name}
                  </Typography>
                </Box>
              )}

              {selectedTrip.vehicule_matricule && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    VÉHICULE
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                    {selectedTrip.vehicule_matricule}
                  </Typography>
                </Box>
              )}

              {selectedTrip.last_mobile_sync && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    DERNIÈRE MISE À JOUR GPS
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#1f2937', mt: 1, fontWeight: 500 }}>
                    {selectedTrip.last_mobile_sync}
                  </Typography>
                </Box>
              )}

              {selectedTrip.pod_photo_url && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadPod(selectedTrip.ID_Voyage)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #3c2151 0%, #5d3477 100%)',
                      boxShadow: '0 2px 8px rgba(60, 33, 81, 0.3)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2a173a 0%, #3c2151 100%)',
                        boxShadow: '0 4px 12px rgba(60, 33, 81, 0.4)',
                      },
                    }}
                  >
                    Télécharger la POD
                  </Button>
                </Box>
              )}
              </Box>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
};

export default ClientDashboard;
